import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import { toast } from "components/ui/toast/Toast";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUpdateCurrentUser } from "lib/hooks/api/users/useUpdateCurrentUser";
import { FieldContainer } from "./utils";

export const NAME_MAX_LENGTH = 100;

const nameField = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(NAME_MAX_LENGTH, `${label} must be ${NAME_MAX_LENGTH} characters or fewer`);

const schema = z.object({
  firstName: nameField("First name"),
  lastName: nameField("Last name"),
});
type NameFormValues = z.infer<typeof schema>;

/** Edit the user's first and last name. */
function ChangeName(): JSX.Element {
  const currentUser = useAssertCurrentUser();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<NameFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: currentUser.firstName ?? "", lastName: currentUser.lastName ?? "" },
  });

  const { mutate: updateUser, isLoading } = useUpdateCurrentUser({
    onSuccess: ({ updateCurrentUser: { firstName, lastName } }) => {
      reset({ firstName, lastName });
      toast.success("Your name has been updated");
    },
    onError: () => {
      toast.error("Could not update your name");
    },
  });

  return (
    <form
      onSubmit={handleSubmit(({ firstName, lastName }) =>
        updateUser({ request: { guid: currentUser.guid, firstName, lastName } }),
      )}
    >
      <Flex column gap={16}>
        <FieldContainer>
          <Label htmlFor="firstName">First name</Label>
          <Input
            control={control}
            name="firstName"
            autoComplete="given-name"
            maxLength={NAME_MAX_LENGTH}
          />
          {errors.firstName && (
            <Text description color="sysDestructiveDefault">
              {errors.firstName.message}
            </Text>
          )}
        </FieldContainer>
        <FieldContainer>
          <Label htmlFor="lastName">Last name</Label>
          <Input
            control={control}
            name="lastName"
            autoComplete="family-name"
            maxLength={NAME_MAX_LENGTH}
          />
          {errors.lastName && (
            <Text description color="sysDestructiveDefault">
              {errors.lastName.message}
            </Text>
          )}
        </FieldContainer>
        <div>
          <Button buttonType="primary" type="submit" loading={isLoading} disabled={!isDirty}>
            Save name
          </Button>
        </div>
      </Flex>
    </form>
  );
}

export default ChangeName;
